import {
	JawAssembly,
	JawAssemblyConfiguration,
} from '../../../interfaces/VersaBuilt.Interface';

// Ported from the standalone multigrip-jaw-configuration-assistant app.
// Width/length comparisons are intentionally loose (==) — the API mixes
// string and numeric representations of the same dimension.

export interface ConfigurationFilters {
	stockShape?: string | null;
	stockWidth?: number | string | null;
	stockLength?: number | string | null;
}

/**
 * The subset of a jaw configuration the page needs to select it and put its
 * wireframe on screen. Satisfied by both the VersaBuilt assemblies payload
 * (`JawAssemblyConfiguration`) and the `cam-data/jaws/` slice
 * (`CamDataJawsConfiguration`).
 */
export interface SelectableJawConfiguration {
	id: string;
	name?: string | null;
	stock_shape?: string | null;
	stock_width?: number | string | null;
	stock_min_length?: number | string | null;
	wireframe_overlay?: string | null;
	jaw_assembly?:
		| JawAssembly
		| { id: string; name?: string | null }
		| string
		| null;
}

export const fitToAspectRatio = (
	containerWidth: number,
	containerHeight: number,
	aspectRatio: number
): { width: number; height: number } | null => {
	if (!containerWidth || !containerHeight || !aspectRatio) return null;
	let width = containerWidth;
	let height = width / aspectRatio;
	if (height > containerHeight) {
		height = containerHeight;
		width = height * aspectRatio;
	}
	return { width, height };
};

export const getConfigStockWidth = (conf: SelectableJawConfiguration) =>
	conf.stock_width ?? null;

export const getConfigMinLength = (conf: SelectableJawConfiguration) =>
	conf.stock_min_length ?? null;

export const matchesConfigurationFilters = (
	conf: JawAssemblyConfiguration,
	{ stockShape, stockWidth, stockLength }: ConfigurationFilters = {}
): boolean => {
	if (stockShape && conf.stock_shape !== stockShape) return false;
	// eslint-disable-next-line eqeqeq
	if (stockWidth != null && getConfigStockWidth(conf) != stockWidth)
		return false;
	// eslint-disable-next-line eqeqeq
	if (stockLength != null && getConfigMinLength(conf) != stockLength)
		return false;
	return true;
};

export const getFilteredConfigurations = (
	jawAssembly: JawAssembly | null | undefined,
	filters: ConfigurationFilters = {}
): JawAssemblyConfiguration[] => {
	if (!jawAssembly?.configurations) return [];
	return jawAssembly.configurations.filter((conf) =>
		matchesConfigurationFilters(conf, filters)
	);
};

export const sortDimensions = (
	a: number | string,
	b: number | string
): number => Number(a) - Number(b);

export const isRoundStockShape = (shape?: string | null): boolean =>
	typeof shape === 'string' && shape.toLowerCase().includes('round');

export const getConfigurationFilters = (
	stockShape?: string | null,
	stockWidth?: number | string | null,
	stockLength?: number | string | null
): ConfigurationFilters => ({
	stockShape,
	stockWidth:
		stockShape && !isRoundStockShape(stockShape) ? stockWidth : null,
	stockLength:
		stockShape && !isRoundStockShape(stockShape) ? stockLength : null,
});

export const getAvailableStockWidths = (
	jawAssembly: JawAssembly | null | undefined,
	stockShape?: string | null
): (number | string)[] => {
	const shapeFiltered = getFilteredConfigurations(jawAssembly, { stockShape });
	return Array.from(
		new Set(
			shapeFiltered
				.map(getConfigStockWidth)
				.filter((value): value is number => value != null)
		)
	).sort(sortDimensions);
};

export const getAvailableStockLengths = (
	jawAssembly: JawAssembly | null | undefined,
	stockShape?: string | null,
	stockWidth?: number | string | null
): (number | string)[] => {
	if (stockWidth == null) return [];
	const shapeFiltered = getFilteredConfigurations(jawAssembly, { stockShape });
	return Array.from(
		new Set(
			shapeFiltered
				// eslint-disable-next-line eqeqeq
				.filter((conf) => getConfigStockWidth(conf) == stockWidth)
				.map(getConfigMinLength)
				.filter((value): value is number => value != null)
		)
	).sort(sortDimensions);
};

export const findConfigurationInJawAssemblies = (
	jawAssemblies: JawAssembly[],
	configId: string
): { jawAssembly: JawAssembly; config: JawAssemblyConfiguration } | null => {
	for (const jawAssembly of jawAssemblies) {
		const config = jawAssembly.configurations?.find(
			(item) => item.id === configId
		);
		if (config) {
			return { jawAssembly, config };
		}
	}
	return null;
};

export const findJawAssemblyForConfiguration = (
	jawAssemblies: JawAssembly[],
	config: SelectableJawConfiguration | null | undefined
): JawAssembly | null => {
	if (!config) return null;

	const jawAssemblyId =
		typeof config.jaw_assembly === 'object'
			? config.jaw_assembly?.id
			: config.jaw_assembly;

	if (jawAssemblyId) {
		const jawAssembly = jawAssemblies.find((item) => item.id === jawAssemblyId);
		if (jawAssembly) return jawAssembly;
	}

	return (
		jawAssemblies.find((jawAssembly) =>
			jawAssembly.configurations?.some((item) => item.id === config.id)
		) ?? null
	);
};

export const normalizeBase64PngToDataUrl = (
	value?: string | null
): string | null => {
	if (!value || typeof value !== 'string') return null;
	if (value.startsWith('data:image/')) return value;
	return `data:image/png;base64,${value}`;
};
